import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import MatchReactions from "@/components/MatchReactions";
import MatchScoreboard from "@/components/MatchScoreboard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, ExternalLink, Vote, Link2, Radio, Server, ChevronDown, ChevronUp, Users as UsersIcon } from "lucide-react";

export default function Results() {
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  const isPrivileged = isAuthed && (user.role === "admin" || user.role === "official");

  const [results, setResults] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [mvpByFixture, setMvpByFixture] = useState({});
  const [voteFor, setVoteFor] = useState(null); // fixture being voted
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [highlightFor, setHighlightFor] = useState(null);
  const [highlightUrl, setHighlightUrl] = useState("");
  const [statsFor, setStatsFor] = useState(null);
  // Iter105 · Expanded Bifrost scoreboard panel (per fixture)
  const [expandedScoreboard, setExpandedScoreboard] = useState(null);
  // Iter81 · Admin/official edit a previously-completed result
  const [editFor, setEditFor] = useState(null);
  const [editHs, setEditHs] = useState(0);
  const [editAs, setEditAs] = useState(0);
  const [editBusy, setEditBusy] = useState(false);
  // Iter86/87 · Per-fixture public CRCON URL + pull
  const [crconFor, setCrconFor] = useState(null);
  const [crconCfg, setCrconCfg] = useState({ stats_url: "", auto_pull: false });
  const [crconUrl, setCrconUrl] = useState("");
  const [crconAuto, setCrconAuto] = useState(false);
  const [crconBusy, setCrconBusy] = useState(false);
  const [crconPulled, setCrconPulled] = useState(null); // {source, rows[], applied?}
  const [crconApplyBusy, setCrconApplyBusy] = useState(false);
  // Iter90 · Frostbite paste flow — Cloudflare blocks server-side fetches,
  // so admins paste the scoreboard copy or DevTools JSON response manually.
  const [crconPaste, setCrconPaste] = useState("");

  const isFrostbiteUrl = (u) => /frostbite\.bifrostgaming\.com|bifrostgaming\.com/i.test(u || "");

  const openCrcon = async (fx) => {
    setCrconFor(fx);
    setCrconUrl("");
    setCrconPaste("");
    setCrconPulled(null);
    try {
      const { data } = await api.get(`/fixtures/${fx.id}/crcon`);
      setCrconCfg(data);
      setCrconUrl(data.stats_url || "");
      setCrconAuto(!!data.auto_pull);
    } catch (e) {
      setCrconCfg({ stats_url: "", auto_pull: false });
    }
  };

  const saveCrcon = async () => {
    if (!crconFor) return;
    setCrconBusy(true);
    try {
      const { data } = await api.put(`/fixtures/${crconFor.id}/crcon`, {
        stats_url: crconUrl.trim(),
        auto_pull: crconAuto,
      });
      setCrconCfg(data);
      toast.success("CRCON link saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setCrconBusy(false);
    }
  };

  const pullCrcon = async () => {
    if (!crconFor) return;
    setCrconBusy(true);
    setCrconPulled(null);
    try {
      // If the admin typed a URL but hasn't hit Save yet, save it first so
      // the pull endpoint has something to work with.
      if (crconUrl.trim() && crconUrl.trim() !== crconCfg.stats_url) {
        await api.put(`/fixtures/${crconFor.id}/crcon`, {
          stats_url: crconUrl.trim(),
          auto_pull: crconAuto,
        });
        setCrconCfg((c) => ({ ...c, stats_url: crconUrl.trim() }));
      }
      const { data } = await api.post(`/fixtures/${crconFor.id}/crcon/pull`);
      if (!data.ok) {
        const err = data.error || "pull failed";
        const friendly = {
          frostbite_fetch_failed: "Couldn't read the Frostbite scoreboard — double-check the URL, or paste the scoreboard rows below as a fallback.",
          invalid_url: "That URL doesn't look like a supported CRCON or Frostbite match page.",
          crcon_unreachable: "CRCON server didn't respond — check the URL and that the match exists.",
          crcon_not_configured: "No stats URL saved yet — paste one and press Save first.",
        }[err];
        toast.error(friendly || `CRCON ${err}`);
        setCrconPulled({ source: data.source || null, rows: [], applied: null });
      } else {
        setCrconPulled({ source: data.source, rows: data.rows || [], applied: data.applied });
        const matched = data.applied?.matched ?? 0;
        const unmatched = data.applied?.unmatched ?? 0;
        if (data.applied?.already_applied) {
          toast.success(`Pulled ${data.rows?.length || 0} rows — stats already applied earlier`);
        } else {
          toast.success(`Pulled ${data.rows?.length || 0} rows · ${matched} matched to rostered players, ${unmatched} unmatched`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull crashed");
    } finally {
      setCrconBusy(false);
    }
  };

  const pasteCrcon = async () => {
    if (!crconFor) return;
    if (!crconPaste.trim()) {
      toast.error("Paste the scoreboard first");
      return;
    }
    setCrconBusy(true);
    setCrconPulled(null);
    try {
      // Make sure the URL is saved so we store a source alongside the rows.
      if (crconUrl.trim() && crconUrl.trim() !== crconCfg.stats_url) {
        try {
          await api.put(`/fixtures/${crconFor.id}/crcon`, {
            stats_url: crconUrl.trim(),
            auto_pull: crconAuto,
          });
          setCrconCfg((c) => ({ ...c, stats_url: crconUrl.trim() }));
        } catch (e) {
          // Not fatal — paste can still proceed without a saved URL.
        }
      }
      const { data } = await api.post(`/fixtures/${crconFor.id}/crcon/paste`, {
        raw: crconPaste,
      });
      if (!data.ok) {
        const err = data.error || "paste failed";
        const friendly = {
          empty_paste: "Paste the scoreboard first.",
          no_rows_parsed: "Couldn't find any player rows in that paste. Expected one player per line with K and D as numbers.",
        }[err];
        toast.error(friendly || `CRCON ${err}`);
        setCrconPulled({ source: data.source || null, rows: [], applied: null });
      } else {
        setCrconPulled({ source: data.source, rows: data.rows || [], applied: data.applied });
        const matched = data.applied?.matched ?? 0;
        const unmatched = data.applied?.unmatched ?? 0;
        if (data.applied?.already_applied) {
          toast.success(`Parsed ${data.rows?.length || 0} rows — stats already applied earlier`);
        } else {
          toast.success(`Parsed ${data.rows?.length || 0} rows · ${matched} matched to rostered players, ${unmatched} unmatched`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paste crashed");
    } finally {
      setCrconBusy(false);
    }
  };

  const reapplyCrcon = async () => {
    if (!crconFor) return;
    if (!window.confirm("Reverse the previous K/D increments on registered players and re-apply from the latest pulled rows?")) return;
    setCrconApplyBusy(true);
    try {
      const { data } = await api.post(`/fixtures/${crconFor.id}/crcon/apply`);
      if (!data.ok) {
        toast.error(`Re-apply · ${data.error || "failed"}`);
      } else {
        setCrconPulled((p) => p ? { ...p, applied: data.applied } : p);
        toast.success(`Re-applied · ${data.applied?.matched ?? 0} matched, ${data.applied?.unmatched ?? 0} unmatched`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-apply crashed");
    } finally {
      setCrconApplyBusy(false);
    }
  };

  const load = async () => {
    const [rs, ts, ps] = await Promise.all([
      api.get("/results"), api.get("/teams"), api.get("/players"),
    ]);
    setResults(rs.data); setTeams(ts.data); setPlayers(ps.data);
    const tallies = await Promise.all(
      rs.data.map((r) => api.get(`/fixtures/${r.id}/mvp`).then((x) => [r.id, x.data]).catch(() => [r.id, null]))
    );
    setMvpByFixture(Object.fromEntries(tallies));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const teamById = (id) => teams.find((t) => t.id === id);
  const playerById = (id) => players.find((p) => p.id === id);

  const rosterFor = (fx) =>
    players.filter((p) => p.team_id === fx.home_team_id || p.team_id === fx.away_team_id);

  const submitVote = async (fx) => {
    if (!selectedPlayer) { toast.error("Choose a player."); return; }
    try {
      await api.post(`/fixtures/${fx.id}/vote`, { player_id: selectedPlayer });
      toast.success("Vote locked in.");
      setVoteFor(null); setSelectedPlayer("");
      const { data } = await api.get(`/fixtures/${fx.id}/mvp`);
      setMvpByFixture((m) => ({ ...m, [fx.id]: data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to vote");
    }
  };

  const saveHighlight = async () => {
    try {
      await api.put(`/fixtures/${highlightFor.id}/highlight`, { highlight_url: highlightUrl });
      toast.success("Highlight attached.");
      setHighlightFor(null); setHighlightUrl("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // Iter81 · Edit a previously-completed result (admin/official only).
  const openEdit = (r) => {
    setEditFor(r); setEditHs(r.home_score); setEditAs(r.away_score);
  };
  const saveEdit = async () => {
    if (!editFor) return;
    setEditBusy(true);
    try {
      const payload = { home_score: Number(editHs), away_score: Number(editAs) };
      const { data } = await api.put(`/fixtures/${editFor.id}/result`, payload);
      toast.success(`Result updated · ${data?.previous?.home_score}–${data?.previous?.away_score} → ${data?.new?.home_score}–${data?.new?.away_score}`);
      setEditFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Edit failed");
    } finally { setEditBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="results-root">
      <div>
        <div className="text-[#FF007F] font-mono tracking-[0.3em] text-xs mb-2">/ RECORDED</div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Results</h1>
        <p className="text-gray-400 mt-2 text-sm">Vote for match MVP · watch the highlights · relive every play.</p>
      </div>

      <div className="space-y-3">
        {results.map((r) => {
          const h = teamById(r.home_team_id), a = teamById(r.away_team_id);
          const homeWin = r.home_score > r.away_score, draw = r.home_score === r.away_score;
          const mvp = mvpByFixture[r.id];
          const leaderId = mvp?.leader_player_id;
          const totalVotes = mvp?.total || 0;
          return (
            <div key={r.id} data-testid={`result-row-${r.id}`}
                 className="border border-[#232330] bg-[#101015] p-5 grid gap-4 md:grid-cols-[1fr_auto_auto]">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[10px] text-gray-500 tracking-widest">
                    {new Date(r.completed_at).toLocaleDateString()}
                  </span>
                  <span className="font-mono text-[10px] text-cyan-400 uppercase tracking-widest">{r.game}</span>
                </div>
                <div className="mt-2 font-heading uppercase tracking-tight text-xl">
                  <span className={homeWin ? "text-[#FF007F]" : "text-gray-300"}>{h?.name}</span>
                  <span className="font-mono text-white mx-3">{r.home_score}–{r.away_score}</span>
                  <span className={!homeWin && !draw ? "text-[#FF007F]" : "text-gray-300"}>{a?.name}</span>
                </div>
                {leaderId && (
                  <div className="mt-2 text-xs text-[#F4D03F] font-mono tracking-widest flex items-center gap-2">
                    <Trophy className="w-3.5 h-3.5" /> MVP {playerById(leaderId)?.handle || "—"}
                    <span className="text-gray-500">· {totalVotes} vote{totalVotes === 1 ? "" : "s"}</span>
                  </div>
                )}
                {/* Iter49 · Match reactions */}
                <div className="mt-3">
                  <MatchReactions fixtureId={r.id} compact />
                </div>
              </div>

              <div className="flex items-center gap-2">
                {r.stats_imported && (
                  <button
                    onClick={() => setStatsFor(r)}
                    data-testid={`view-server-stats-${r.id}`}
                    className="flex items-center gap-2 px-3 py-2 border border-[#00FFB2] text-[#00FFB2] hover:bg-[#00FFB2] hover:text-black transition-colors text-xs font-bold uppercase tracking-widest"
                  >
                    <Radio className="w-3.5 h-3.5" /> Server stats
                  </button>
                )}
                {r.highlight_url ? (
                  <a
                    href={r.highlight_url} target="_blank" rel="noreferrer"
                    data-testid={`highlight-link-${r.id}`}
                    className="flex items-center gap-2 px-3 py-2 border border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black transition-colors text-xs font-bold uppercase tracking-widest"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Highlights
                  </a>
                ) : (
                  <span className="text-[10px] text-gray-600 font-mono tracking-widest uppercase">No clip yet</span>
                )}
                {isPrivileged && (
                  <Button
                    data-testid={`edit-result-${r.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(r)}
                    className="rounded-none border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F] hover:text-white uppercase tracking-widest text-xs font-bold"
                  >
                    <Trophy className="w-3.5 h-3.5 mr-1.5" /> Edit result
                  </Button>
                )}
                {r.crcon_config?.stats_url && (
                  <Button
                    data-testid={`crcon-public-${r.id}`}
                    asChild
                    variant="outline"
                    size="sm"
                    className="rounded-none border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black uppercase tracking-widest text-xs font-bold"
                  >
                    <a href={r.crcon_config.stats_url} target="_blank" rel="noreferrer">
                      <Server className="w-3.5 h-3.5 mr-1.5" /> Scoreboard
                    </a>
                  </Button>
                )}
                {(r.crcon_last_pull?.rows?.length || 0) > 0 && (
                  <Button
                    data-testid={`scoreboard-toggle-${r.id}`}
                    onClick={() => setExpandedScoreboard((cur) => (cur === r.id ? null : r.id))}
                    variant="outline"
                    size="sm"
                    className="rounded-none border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F] hover:text-white uppercase tracking-widest text-xs font-bold"
                  >
                    <UsersIcon className="w-3.5 h-3.5 mr-1.5" />
                    {r.crcon_last_pull.rows.length} players
                    {expandedScoreboard === r.id
                      ? <ChevronUp className="w-3.5 h-3.5 ml-1.5" />
                      : <ChevronDown className="w-3.5 h-3.5 ml-1.5" />}
                  </Button>
                )}
                {isPrivileged && (
                  <Button
                    data-testid={`crcon-config-${r.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => openCrcon(r)}
                    className="rounded-none border-[#F4D03F] text-[#F4D03F] hover:bg-[#F4D03F] hover:text-black uppercase tracking-widest text-xs font-bold"
                  >
                    <Server className="w-3.5 h-3.5 mr-1.5" /> CRCON
                  </Button>
                )}
                {isPrivileged && (
                  <Dialog open={highlightFor?.id === r.id} onOpenChange={(o) => !o && setHighlightFor(null)}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid={`edit-highlight-${r.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => { setHighlightFor(r); setHighlightUrl(r.highlight_url || ""); }}
                        className="rounded-none border-[#232330] hover:border-cyan-400"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
                      <DialogHeader>
                        <DialogTitle className="font-heading uppercase tracking-tight">Attach highlight clip</DialogTitle>
                      </DialogHeader>
                      <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Clip URL</Label>
                      <Input
                        data-testid="highlight-url-input"
                        value={highlightUrl} onChange={(e) => setHighlightUrl(e.target.value)}
                        placeholder="https://youtube.com/watch?v=..."
                        className="rounded-none bg-[#05050A] border-[#232330]"
                      />
                      <Button
                        onClick={saveHighlight}
                        data-testid="save-highlight"
                        className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
                      >
                        Save clip
                      </Button>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              <div>
                {isAuthed ? (
                  <Dialog open={voteFor?.id === r.id} onOpenChange={(o) => !o && setVoteFor(null)}>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => { setVoteFor(r); setSelectedPlayer(mvp?.my_vote || ""); }}
                        data-testid={`vote-mvp-${r.id}`}
                        className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold text-xs"
                      >
                        <Vote className="w-3.5 h-3.5 mr-2" />
                        {mvp?.my_vote ? "Change vote" : "Vote MVP"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
                      <DialogHeader>
                        <DialogTitle className="font-heading uppercase tracking-tight">
                          MVP · {h?.tag} vs {a?.tag}
                        </DialogTitle>
                      </DialogHeader>
                      <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Pick your player</Label>
                      <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
                        <SelectTrigger data-testid="mvp-player-select" className="rounded-none bg-[#05050A] border-[#232330]">
                          <SelectValue placeholder="Choose from either roster" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#101015] border-[#232330] text-white">
                          {rosterFor(r).map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.handle} · {teamById(p.team_id)?.tag}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={() => submitVote(r)}
                        data-testid="confirm-vote"
                        className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold"
                      >
                        Lock in vote
                      </Button>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <div className="text-[10px] text-gray-500 font-mono tracking-widest uppercase">Login to vote</div>
                )}
              </div>
              {expandedScoreboard === r.id && (r.crcon_last_pull?.rows?.length || 0) > 0 && (
                <div className="md:col-span-3 mt-2">
                  <MatchScoreboard
                    rows={r.crcon_last_pull.rows}
                    pulledAt={r.crcon_last_pull.pulled_at}
                    source={r.crcon_config?.stats_url}
                  />
                </div>
              )}
            </div>
          );
        })}
        {results.length === 0 && (
          <div className="border border-[#232330] p-8 text-center text-gray-500">No results recorded yet.</div>
        )}
      </div>

      {/* Iter26: server-stats detail dialog */}
      <Dialog open={!!statsFor} onOpenChange={(o) => !o && setStatsFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none" data-testid="server-stats-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Radio className="w-4 h-4 text-[#00FFB2]" /> Server stat pull
            </DialogTitle>
          </DialogHeader>
          {statsFor && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-[#232330] p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Source</div>
                  <div className="font-mono text-lg mt-1 text-[#00FFB2]" data-testid="stats-source">
                    {(statsFor.stats_source || "—").toUpperCase()}
                  </div>
                </div>
                <div className="border border-[#232330] p-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">When</div>
                  <div className="font-mono text-xs mt-1">{statsFor.stats_imported_at ? new Date(statsFor.stats_imported_at).toLocaleString() : "—"}</div>
                </div>
              </div>
              {statsFor.stats_fallback && (
                <div className="border border-[#F4D03F]/60 bg-[#F4D03F]/5 p-3" data-testid="stats-fallback">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">Fallback</div>
                  <div className="font-mono text-xs mt-1 text-white break-all">{statsFor.stats_fallback}</div>
                  <div className="text-[10px] text-gray-400 mt-2 font-mono">
                    Paste real HLL RCON creds into <span className="text-[#00FFB2]">backend/.env</span> (HLL_RCON_HOST / PORT / PASSWORD) then run <span className="text-[#00FFB2]">supervisorctl restart backend</span>.
                  </div>
                </div>
              )}
              <div className="border border-[#232330] p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Rostered player deltas</div>
                <div className="text-xs font-mono text-gray-400">
                  {players.filter((p) => p.team_id === statsFor.home_team_id || p.team_id === statsFor.away_team_id).length} players imported ·
                  view individual K/D/A on the <a href="/players" className="text-cyan-400 hover:underline">Players page</a>.
                </div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Imported by · {statsFor.stats_imported_by || "—"}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter81 · Edit result dialog */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none" data-testid="edit-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Edit result</DialogTitle>
          </DialogHeader>
          {editFor && (
            <div className="space-y-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                {teamById(editFor.home_team_id)?.tag || "?"} vs {teamById(editFor.away_team_id)?.tag || "?"} · currently {editFor.home_score}–{editFor.away_score}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">{teamById(editFor.home_team_id)?.tag || "Home"} score</Label>
                  <Input
                    type="number"
                    min={0}
                    max={5}
                    data-testid="edit-result-home"
                    value={editHs}
                    onChange={(e) => setEditHs(e.target.value)}
                    className="rounded-none bg-[#05050A] border-[#232330]"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">{teamById(editFor.away_team_id)?.tag || "Away"} score</Label>
                  <Input
                    type="number"
                    min={0}
                    max={5}
                    data-testid="edit-result-away"
                    value={editAs}
                    onChange={(e) => setEditAs(e.target.value)}
                    className="rounded-none bg-[#05050A] border-[#232330]"
                  />
                </div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
                Standings + player stats will be rewound and re-applied. Allowed scores: 5-0, 4-1, 3-2.
              </div>
              <Button
                onClick={saveEdit}
                disabled={editBusy}
                data-testid="edit-result-save"
                className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold"
              >
                {editBusy ? "Updating…" : "Save corrected result"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Iter86/87 · Per-fixture public CRCON URL (admin/official only). No API key. */}
      <Dialog open={!!crconFor} onOpenChange={(o) => { if (!o) { setCrconFor(null); setCrconPulled(null); }}}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-2xl" data-testid="crcon-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Server className="w-4 h-4 text-[#F4D03F]" /> CRCON · match server data pull
            </DialogTitle>
          </DialogHeader>
          {crconFor && (
            <div className="space-y-4 text-sm">
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                {teamById(crconFor.home_team_id)?.tag || "?"} vs {teamById(crconFor.away_team_id)?.tag || "?"}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Public game URL</Label>
                <Input
                  data-testid="crcon-stats-url"
                  value={crconUrl}
                  onChange={(e) => setCrconUrl(e.target.value)}
                  placeholder="https://9pz-stats.hlladmin.com/games/4217"
                  className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono text-xs"
                />
                <div className="text-[9px] font-mono text-gray-600 mt-1">
                  Paste the match's direct CRCON stats page (e.g. <code>/games/4217</code>) <em>or</em> a Frostbite / Bifrost Gaming match page (<code>frostbite.bifrostgaming.com/hll/.../&lt;match_id&gt;</code>). No API key needed — logs are already public.
                </div>
                {isFrostbiteUrl(crconUrl) && (
                  <div className="text-[9px] font-mono text-cyan-400/80 mt-1" data-testid="crcon-frostbite-hint">
                    ✓ Frostbite auto-pull is live — scoreboard will be fetched automatically. The paste box below stays as a fallback if the page ever changes.
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer" data-testid="crcon-auto-pull-row">
                <input
                  type="checkbox"
                  checked={crconAuto}
                  onChange={(e) => setCrconAuto(e.target.checked)}
                  className="accent-[#F4D03F]"
                  data-testid="crcon-auto-pull-checkbox"
                />
                <span className="font-mono text-[10px] uppercase tracking-widest">
                  Auto-pull on result submission
                </span>
              </label>

              {isFrostbiteUrl(crconUrl) && (
                <div data-testid="crcon-paste-block">
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">Paste Frostbite scoreboard</Label>
                  <textarea
                    data-testid="crcon-paste-input"
                    value={crconPaste}
                    onChange={(e) => setCrconPaste(e.target.value)}
                    placeholder={"Open the match on Frostbite, select the player table rows, copy them, and paste here.\n\nExamples that work:\nFox      25  5\nBear     10  10\n\n…or paste the raw JSON from DevTools (Network → the stats response → Copy as JSON)."}
                    className="w-full h-40 rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 font-mono text-[11px] leading-relaxed"
                  />
                  <div className="text-[9px] font-mono text-gray-600 mt-1">
                    First number per row = <span className="text-[#F4D03F]">kills</span>, second = <span className="text-[#FF007F]">deaths</span>. Extra columns after that are ignored.
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={saveCrcon}
                  disabled={crconBusy || !crconUrl.trim()}
                  data-testid="crcon-save"
                  className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold"
                >
                  {crconBusy ? "Saving…" : "Save link"}
                </Button>
                {isFrostbiteUrl(crconUrl) ? (
                  <Button
                    onClick={pasteCrcon}
                    disabled={crconBusy || !crconPaste.trim()}
                    data-testid="crcon-paste-submit"
                    variant="outline"
                    className="rounded-none border-[#00FFB2] text-[#00FFB2] hover:bg-[#00FFB2] hover:text-black uppercase tracking-widest text-xs font-bold"
                  >
                    <Radio className="w-3.5 h-3.5 mr-1.5" /> {crconBusy ? "Parsing…" : "Parse scoreboard"}
                  </Button>
                ) : (
                  <Button
                    onClick={pullCrcon}
                    disabled={crconBusy || !crconUrl.trim()}
                    data-testid="crcon-pull"
                    variant="outline"
                    className="rounded-none border-[#00FFB2] text-[#00FFB2] hover:bg-[#00FFB2] hover:text-black uppercase tracking-widest text-xs font-bold"
                  >
                    <Radio className="w-3.5 h-3.5 mr-1.5" /> {crconBusy ? "Pulling…" : "Pull stats now"}
                  </Button>
                )}
              </div>

              {crconPulled && (
                <div className="border border-[#232330] p-3 max-h-96 overflow-auto" data-testid="crcon-pulled-rows">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2 break-all">
                    Source · <span className="text-[#00FFB2]">{crconPulled.source || "—"}</span> · {crconPulled.rows.length} players
                  </div>
                  {crconPulled.applied && (
                    <div className="flex items-center gap-3 flex-wrap mb-3 pb-3 border-b border-[#232330]/60" data-testid="crcon-applied-summary">
                      <div className="text-[10px] font-mono uppercase tracking-widest">
                        <span className="text-[#00FFB2]">{crconPulled.applied.matched ?? 0}</span>
                        <span className="text-gray-500"> matched to roster · </span>
                        <span className="text-[#FF007F]">{crconPulled.applied.unmatched ?? 0}</span>
                        <span className="text-gray-500"> unmatched</span>
                        {crconPulled.applied.already_applied && (
                          <span className="ml-2 text-[#F4D03F]">· already applied</span>
                        )}
                      </div>
                      <Button
                        onClick={reapplyCrcon}
                        disabled={crconApplyBusy}
                        data-testid="crcon-reapply"
                        variant="outline"
                        size="sm"
                        className="rounded-none border-[#F4D03F] text-[#F4D03F] hover:bg-[#F4D03F] hover:text-black uppercase tracking-widest text-[10px] font-bold ml-auto"
                      >
                        {crconApplyBusy ? "Re-applying…" : "Re-apply to players"}
                      </Button>
                    </div>
                  )}
                  {crconPulled.rows.length === 0 ? (
                    <div className="text-xs text-gray-500 font-mono">No rows returned. Double-check the URL.</div>
                  ) : (
                    <table className="w-full text-xs font-mono">
                      <thead className="sticky top-0 bg-[#101015]">
                        <tr className="text-left text-[9px] uppercase tracking-widest text-gray-500">
                          <th className="pb-1">Handle</th>
                          <th className="pb-1 text-right">K</th>
                          <th className="pb-1 text-right">D</th>
                          <th className="pb-1 text-right">K/D</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crconPulled.rows.map((row, i) => {
                          const matchedHandles = new Set((crconPulled.applied?.matched_rows || []).map((m) => m.crcon_handle));
                          const isMatched = matchedHandles.has(row.handle);
                          return (
                            <tr key={i} className="border-t border-[#232330]/60">
                              <td className="py-1 truncate max-w-[220px] flex items-center gap-1.5">
                                {crconPulled.applied && (
                                  <span className={`inline-block w-1.5 h-1.5 ${isMatched ? "bg-[#00FFB2]" : "bg-[#FF007F]/60"}`} />
                                )}
                                {row.handle}
                              </td>
                              <td className="py-1 text-right text-[#00FFB2]">{row.kills}</td>
                              <td className="py-1 text-right text-[#FF007F]">{row.deaths}</td>
                              <td className="py-1 text-right">{row.kd}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
